/**
 * Application Configuration
 *
 * This file contains all application configuration that should NOT be in environment variables.
 * Environment variables should only contain secrets and environment-specific values.
 *
 * Configuration Philosophy:
 * - Secrets (API keys, tokens) → .env
 * - Configuration (domains, features, settings) → This file
 */

// =============================================================================
// DOMAIN CONFIGURATION
// =============================================================================

/**
 * Route Royal main domains that should show the marketing site
 * These are the domains where Route Royal itself is hosted
 */
export const ROUTE_ROYAL_MAIN_DOMAINS = [
  'routeroyal.com',
  'www.routeroyal.com',
] as const;

/**
 * Domain suffixes used for tenant subdomains
 * e.g., "allys.routeroyal.com" uses suffix "routeroyal.com"
 */
export const TENANT_DOMAIN_SUFFIXES = ['routeroyal.com'] as const;

/**
 * Admin portal subdomains that should be excluded from tenant routing
 * These are handled by a separate Vercel project
 */
export const ADMIN_PORTAL_SUBDOMAINS = [
  'portal',
  'helm',
  'admin',
  'api',
] as const;

/**
 * Development domains that should be treated specially
 */
export const DEVELOPMENT_DOMAINS = ['localhost', '127.0.0.1'] as const;

// =============================================================================
// LOCALE CONFIGURATION
// =============================================================================

/**
 * Supported locales across all tenants
 * Individual tenants may support a subset of these
 */
export const SUPPORTED_LOCALES = ['en', 'es', 'fr'] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];

/**
 * Default locale when none is specified
 */
export const DEFAULT_LOCALE: Locale = 'en';

/**
 * Locale display names for UI
 */
export const LOCALE_NAMES: Record<Locale, string> = {
  en: 'English',
  es: 'Español',
  fr: 'Français',
};

/**
 * Locale to country code mapping for flags/icons
 */
export const LOCALE_COUNTRIES: Record<Locale, string> = {
  en: 'GB',
  es: 'ES',
  fr: 'FR',
};

// =============================================================================
// CURRENCY CONFIGURATION
// =============================================================================

/**
 * Supported currencies
 */
export const SUPPORTED_CURRENCIES = [
  'USD',
  'EUR',
  'GBP',
  'MUR',
  'ZAR',
  'AUD',
] as const;
export type Currency = (typeof SUPPORTED_CURRENCIES)[number];

/**
 * Default currency
 */
export const DEFAULT_CURRENCY: Currency = 'USD';

/**
 * Currency display configuration
 */
export const CURRENCY_CONFIG: Record<
  Currency,
  {
    symbol: string;
    name: string;
    locale: string;
    decimalPlaces: number;
  }
> = {
  USD: { symbol: '$', name: 'US Dollar', locale: 'en-US', decimalPlaces: 0 },
  EUR: { symbol: '€', name: 'Euro', locale: 'de-DE', decimalPlaces: 0 },
  GBP: {
    symbol: '£',
    name: 'British Pound',
    locale: 'en-GB',
    decimalPlaces: 0,
  },
  MUR: {
    symbol: 'Rs',
    name: 'Mauritian Rupee',
    locale: 'en-MU',
    decimalPlaces: 0,
  },
  ZAR: {
    symbol: 'R',
    name: 'South African Rand',
    locale: 'en-ZA',
    decimalPlaces: 0,
  },
  AUD: {
    symbol: 'A$',
    name: 'Australian Dollar',
    locale: 'en-AU',
    decimalPlaces: 0,
  },
};

// =============================================================================
// THEME CONFIGURATION
// =============================================================================

/**
 * Available themes in the system
 */
export const AVAILABLE_THEMES = ['allys', 'royale', 'route-royal', 'stone-investment', 'stone-investments'] as const;
export type ThemeId = (typeof AVAILABLE_THEMES)[number];

/**
 * Default theme when none is specified
 */
export const DEFAULT_THEME: ThemeId = 'royale';

const STONE_THEME_IDS: readonly string[] = ['stone-investment', 'stone-investments'];

/**
 * Get the supported locales for a given theme.
 * Stone only supports EN/FR; other themes support all locales.
 */
export function getLocalesForTheme(themeId: string | null | undefined): Locale[] {
  if (themeId && STONE_THEME_IDS.includes(themeId)) {
    return ['en', 'fr'];
  }
  return [...SUPPORTED_LOCALES];
}

// =============================================================================
// CACHE CONFIGURATION
// =============================================================================

/**
 * Cache TTL settings (in milliseconds)
 */
export const CACHE_CONFIG = {
  /** Tenant config cache TTL (1 minute) */
  tenantConfig: 60 * 1000,

  /** Site config cache TTL (5 minutes) */
  siteConfig: 5 * 60 * 1000,

  /** Properties list cache TTL (1 minute) */
  properties: 60 * 1000,

  /** Projects list cache TTL (5 minutes) */
  projects: 5 * 60 * 1000,

  /** Blog/News cache TTL (10 minutes) */
  blog: 10 * 60 * 1000,

  /** Static content cache TTL (1 hour) */
  staticContent: 60 * 60 * 1000,
} as const;

/**
 * ISR revalidation settings (in seconds)
 */
export const ISR_REVALIDATE = {
  /** Homepage revalidation */
  home: 60,

  /** Properties listing revalidation */
  properties: 60,

  /** Property detail revalidation */
  propertyDetail: 300,

  /** Projects listing revalidation */
  projects: 300,

  /** Blog/News revalidation */
  blog: 600,

  /** Static pages (about, contact) revalidation */
  static: 3600,
} as const;

// =============================================================================
// PREVIEW MODE CONFIGURATION
// =============================================================================

/**
 * Preview mode settings
 */
export const PREVIEW_CONFIG = {
  /** Cookie name for storing preview organisation ID */
  orgCookieName: 'rr_preview_org',

  /** Cookie name for storing preview site ID */
  siteCookieName: 'rr_preview_site',

  /** Preview cookie max age (1 hour in seconds) */
  cookieMaxAge: 60 * 60,

  /** Query parameter for preview organisation ID */
  queryParam: 'preview_org_id',
} as const;

// =============================================================================
// API CONFIGURATION
// =============================================================================

/**
 * API endpoint configuration
 * The actual API URL comes from environment variables (secret/environment-specific)
 */
export const API_CONFIG = {
  /** API version prefix */
  versionPrefix: '/api/v1',

  /** Request timeout (30 seconds) */
  timeout: 30000,

  /** Endpoints excluded from version prefix */
  excludedPaths: ['/oauth/token', '/metrics'],

  /** Retry configuration */
  retry: {
    maxRetries: 3,
    retryDelay: 1000,
    retryOn: [502, 503, 504],
  },
} as const;

// =============================================================================
// FEATURE FLAGS
// =============================================================================

/**
 * Feature flags configuration
 * These can be overridden by environment variables for specific environments
 */
export const FEATURE_FLAGS = {
  /** Enable Edge Config for tenant caching */
  useEdgeConfig: true,

  /** Enable ISR for static pages */
  useISR: true,

  /** Enable on-demand revalidation via webhooks */
  useOnDemandRevalidation: true,

  /** Enable debug logging in production */
  debugLogging: false,

  /** Enable AI Spaces feature */
  aiSpaces: false,

  /** Enable multi-currency support */
  multiCurrency: true,
} as const;

// =============================================================================
// NAVIGATION CONFIGURATION
// =============================================================================

/**
 * Default navigation items for tenant sites
 * This defines the structure; labels come from translations
 */
export const DEFAULT_NAVIGATION = [
  { key: 'home', path: '' },
  { key: 'properties', path: '/properties' },
  { key: 'projects', path: '/projects' },
  { key: 'news', path: '/news' },
  { key: 'about', path: '/about' },
  { key: 'contact', path: '/contact' },
] as const;

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Check if a domain is a Route Royal main domain
 */
export function isRouteRoyalMainDomain(hostname: string): boolean {
  const normalizedHostname = hostname.toLowerCase().replace(/^www\./, '');
  return ROUTE_ROYAL_MAIN_DOMAINS.some(domain => {
    const normalizedDomain = domain.toLowerCase().replace(/^www\./, '');
    return normalizedHostname === normalizedDomain;
  });
}

/**
 * Check if a domain is an admin portal subdomain
 */
export function isAdminPortalDomain(hostname: string): boolean {
  const subdomain = hostname.split('.')[0]?.toLowerCase();
  return ADMIN_PORTAL_SUBDOMAINS.includes(
    subdomain as (typeof ADMIN_PORTAL_SUBDOMAINS)[number]
  );
}

/**
 * Check if a domain is a development domain
 */
export function isDevelopmentDomain(hostname: string): boolean {
  const cleanHostname = hostname.split(':')[0]?.toLowerCase() || '';
  return DEVELOPMENT_DOMAINS.some(dev => cleanHostname.includes(dev));
}

/**
 * Check if a domain is a tenant subdomain (e.g., allys.routeroyal.com)
 */
export function isTenantSubdomain(hostname: string): boolean {
  const cleanHostname = hostname.toLowerCase();
  return TENANT_DOMAIN_SUFFIXES.some(suffix => {
    if (cleanHostname === suffix || cleanHostname === `www.${suffix}`) {
      return false; // This is the main domain, not a subdomain
    }
    return cleanHostname.endsWith(`.${suffix}`);
  });
}

/**
 * Branch domain suffixes for development/staging environments
 * e.g., "allys-dev.routeroyal.com" (develop branch)
 *       "allys-staging.routeroyal.com" (staging branch)
 */
export const BRANCH_SUFFIXES = ['-dev', '-staging'] as const;
export type BranchSuffix = (typeof BRANCH_SUFFIXES)[number];

/**
 * Extract tenant slug from subdomain
 * e.g., "allys.routeroyal.com" → "allys"
 * e.g., "allys-dev.routeroyal.com" → "allys" (with branch: "-dev")
 */
export function extractTenantSlug(hostname: string): string | null {
  const cleanHostname = hostname.toLowerCase();
  for (const suffix of TENANT_DOMAIN_SUFFIXES) {
    if (cleanHostname.endsWith(`.${suffix}`)) {
      let slug = cleanHostname.replace(`.${suffix}`, '');

      // Strip branch suffix if present (e.g., "allys-dev" → "allys")
      for (const branchSuffix of BRANCH_SUFFIXES) {
        if (slug.endsWith(branchSuffix)) {
          slug = slug.slice(0, -branchSuffix.length);
          break;
        }
      }

      if (
        slug &&
        !ADMIN_PORTAL_SUBDOMAINS.includes(
          slug as (typeof ADMIN_PORTAL_SUBDOMAINS)[number]
        )
      ) {
        return slug;
      }
    }
  }
  return null;
}

/**
 * Extract branch suffix from hostname
 * e.g., "allys-dev.routeroyal.com" → "-dev"
 * e.g., "allys.routeroyal.com" → null
 */
export function extractBranchSuffix(hostname: string): BranchSuffix | null {
  const cleanHostname = hostname.toLowerCase();
  for (const suffix of TENANT_DOMAIN_SUFFIXES) {
    if (cleanHostname.endsWith(`.${suffix}`)) {
      const subdomain = cleanHostname.replace(`.${suffix}`, '');
      for (const branchSuffix of BRANCH_SUFFIXES) {
        if (subdomain.endsWith(branchSuffix)) {
          return branchSuffix;
        }
      }
    }
  }
  return null;
}

/**
 * Check if a domain is a branch-specific domain (dev or staging)
 */
export function isBranchDomain(hostname: string): boolean {
  return extractBranchSuffix(hostname) !== null;
}

/**
 * Check if a locale is supported
 */
export function isValidLocale(locale: string): locale is Locale {
  return SUPPORTED_LOCALES.includes(locale as Locale);
}

/**
 * Check if a currency is supported
 */
export function isValidCurrency(currency: string): currency is Currency {
  return SUPPORTED_CURRENCIES.includes(currency as Currency);
}

/**
 * Format price with currency
 */
export function formatPrice(
  amount: number | null | undefined,
  currency: Currency = DEFAULT_CURRENCY,
  locale?: Locale
): string {
  if (amount == null) return 'Price on request';

  const config = CURRENCY_CONFIG[currency];
  return new Intl.NumberFormat(
    locale ? `${locale}-${LOCALE_COUNTRIES[locale]}` : config.locale,
    {
      style: 'currency',
      currency,
      minimumFractionDigits: config.decimalPlaces,
      maximumFractionDigits: config.decimalPlaces,
    }
  ).format(amount);
}
